import { useContext, useEffect, useState } from 'react';
import { UserContext } from 'src/contexts/UserContext';
import { getTenantSettings } from '../../graphql/queries';
import { updateTenantSettings } from '../../graphql/mutations';
import { Formik } from 'formik';
import { enqueueSnackbar } from 'notistack';
import React from 'react';
import { cardStyle } from 'src/theme';
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	CircularProgress,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { generateClient, get, post } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';
// import ChimePhoneNumberDialog from "./ChimePhoneNumberDialog";

const PURE_CLOUD_REGION_HOSTS = {
	us_east_1: 'mypurecloud.com',
	eu_west_1: 'mypurecloud.ie',
	ap_southeast_2: 'mypurecloud.com.au',
	ap_northeast_1: 'mypurecloud.jp',
	eu_central_1: 'mypurecloud.de',
	us_west_2: 'usw2.pure.cloud',
	ca_central_1: 'cac1.pure.cloud',
	ap_northeast_2: 'apne2.pure.cloud',
	eu_west_2: 'euw2.pure.cloud',
	ap_south_1: 'aps1.pure.cloud',
	us_east_2: 'use2.us-gov-pure.cloud',
	sa_east_1: 'sae1.pure.cloud',
};

export default function Genesys() {
	const client = generateClient();
	const useSingleColumn = useMediaQuery('(max-width:845px)');
	const userContext = useContext(UserContext);
	const [genesysSettings, setGenesysSettings] = useState({
		cloudRegion: 'usw2.pure.cloud',
		clientId: '',
		clientSecret: '',
		queueId: '',
		wrapUps: '',
	});
	const [tenantSettings, setTenantSettings] = useState({});
	const [queues, setQueues] = useState([]);
	const [queueId, setQueueId] = useState('');
	const [wrapups, setWrapups] = useState([]);
	const [flowId, setFlowId] = useState('');
	const [flows, setFlows] = useState([]);
	const [selectedWrapUpCodeId, setSelectedWrapCodeId] = useState('');
	const [testSuccess, setTestSuccess] = useState(false);
	const [popWindow, setPopWindow] = useState(null);
	const [redirectUrl, setRedirectUrl] = useState('');
	const [tokens, setTokens] = useState({});

	const [savingContactCenterValues, setSavingContactCenterValues] =
		useState(false);
	const [
		contactCenterConnectionSucceeded,
		setContactCenterConnectionSucceeded,
	] = useState(false);
	const [testingContactCenterConnection, setTestingContactCenterConnection] =
		useState(false);

	const [channelValidations, setChannelValidations] = useState([]);

	/** Whether the connection test is in progress. */
	const [testingConnection, setTestingConnection] = useState(false);

	/** Whether the permissions dialog is open. */
	const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);

	// const [chimePhoneNumberDialogOpen, setChimePhoneNumberDialogOpen] = useState(false);
	const [currentOutboundPhoneNumber, setCurrentOutboundPhoneNumber] =
		useState();

	useEffect(() => {
		async function getData() {
			try {
				const graphqlResponse = await client.graphql({
					query: getTenantSettings,
					variables: { id: userContext.tenantId },
				});
				const tenantSettings = graphqlResponse.data.getTenantSettings;
				setCurrentOutboundPhoneNumber(
					tenantSettings.telephoneProviderInfo.genesys.chime
						?.outboundDialNumber,
				);
				setTenantSettings(tenantSettings);
				if (
					tenantSettings.telephoneProviderInfo &&
					tenantSettings.telephoneProviderInfo.genesys
				) {
					setGenesysSettings({
						...tenantSettings.telephoneProviderInfo.genesys,
					});
					const queues = await get({
						apiName: 'cdyxoutreach',
						path: '/cci/genesys/campaigns',
						options: {
							headers: {
								Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
								'x-api-key': userContext.apiKey,
								'Content-Type': 'application/json',
							},
						},
					}).response;

					if (queues.length > 0) {
						setQueues(queues);
					}
				}
			} catch (err) {
				console.error('Error loading Genesys tenant settings', err);
			}
		}
		if (userContext.telephonyProvider === 'Genesys') {
			getData();
		}
	}, [userContext.telephonyProvider]);

	useEffect(() => {
		if (!popWindow) {
			return;
		}

		let requestSent = false; // Only make one test request.

		// Keep checking the popup URL for the authorization code.
		const timer = setInterval(async () => {
			// Nothing to do once the popup is closed.
			if (!popWindow) {
				timer && clearInterval(timer);
				return;
			}

			// If there's a request in progress, don't make another one.
			if (requestSent) {
				return;
			}

			// Check for the authorization code.
			const currentUrl = popWindow.location.href;
			if (!currentUrl) {
				return;
			}
			const searchParams = new URL(currentUrl).searchParams;
			const authorizationCode = searchParams.get('code');
			if (!authorizationCode) {
				return;
			}
			popWindow.close();
			console.log(`The popup URL has URL code param = ${authorizationCode}.`);

			// Test the connection.
			try {
				requestSent = true;
				const testResponse = await post({
					apiName: 'cdyxoutreach',
					path: '/cci/genesys/test-connection',
					options: {
						body: {
							authorizationCode,
							...genesysSettings,
							redirectUrl,
						},
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
							'Content-Type': 'application/json',
						},
					},
				}).response;

				setChannelValidations(testResponse.validations);
				setTokens(testResponse.tokens);
				setPermissionsDialogOpen(true);
				setTestSuccess(true);
			} catch (connectionTestError) {
				setTestSuccess(false);
				enqueueSnackbar(
					'Connection unsuccessful. Please check your connection settings.',
					{
						variant: 'error',
						autoHideDuration: 5000,
					},
				);
				console.error(
					'Error authenticating with Genesys:',
					connectionTestError,
				);
			}

			clearInterval(timer);
			setPopWindow(null);
			setTestingConnection(false);
		}, 500);
	}, [popWindow]);

	useEffect(() => {
		async function getWrapUps(id) {
			const wrapups = await get({
				apiName: 'cdyxoutreach',
				path: `/cci/genesys/wrapups/${id}`,
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;

			if (wrapups.length > 0) {
				setWrapups(wrapups);
			}
		}

		if (queueId) {
			getWrapUps(queueId);
		}
	}, [queueId]);

	/**
	 * Uses the provided Genesys credentials to test the connection.
	 *
	 * @param {*} values The form values for the Genesys credentials.
	 */
	const testConnection = async (values) => {
		setTestingConnection(true);
		setGenesysSettings(values);
		try {
			const testResult = await post({
				apiName: 'cdyxoutreach',
				path: '/cci/genesys/test-connection',
				options: {
					body: {
						...values,
					},
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
						'Content-Type': 'application/json',
					},
				},
			}).response;
			setTestingConnection(false);
			setTestSuccess(true);
			setContactCenterConnectionSucceeded(true);
			enqueueSnackbar('Connection Test Successful', { variant: 'success' });
		} catch (err) {
			console.error('Error testing Genesys connection', err);
			setTestingConnection(false);
			setTestSuccess(false);
			enqueueSnackbar('Connection Test Failed', { variant: 'error' });
		}
	};

	async function saveTenantSettings(values, authSettings = false) {
		try {
			setGenesysSettings(values);
			delete tenantSettings.createdAt;
			delete tenantSettings.updatedAt;
			delete tenantSettings.__typename;
			tenantSettings.telephonyProvider = 'Genesys';
			if (!tenantSettings.telephoneProviderInfo) {
				tenantSettings.telephoneProviderInfo = {};
			} else {
				// Remove any other set providers
				delete tenantSettings.telephoneProviderInfo.connect;
				delete tenantSettings.telephoneProviderInfo.nobelbiz;
				delete tenantSettings.telephoneProviderInfo.incontact;

				// TODO: Remove this when InContact settings are only in TelephonyProviderInfo
				const icTenantSettingsToRemove = [
					'icApp',
					'icVendor',
					'icBU',
					'icUserName',
					'icPassword',
					'icGetAuthTokenURL',
					'icAccessKey',
					'icAppSecret',
					'icDiscoveryDomain',
					'icScriptName',
				];
				icTenantSettingsToRemove.forEach((setting) => {
					tenantSettings[setting] = null;
				});
			}
			tenantSettings.telephoneProviderInfo.genesys = values;
			await client.graphql({
				query: updateTenantSettings,
				variables: { input: tenantSettings },
			});

			// Only if we're saving the the auth settings.
			if (authSettings) {
				await post({
					apiName: 'cdyxoutreach',
					path: '/cci/genesys/save-connection',
					options: {
						body: {},
						headers: {
							Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
							'x-api-key': userContext.apiKey,
							'Content-Type': 'application/json',
						},
					},
				}).response;
			}
			enqueueSnackbar('Save Successful.');
		} catch (err) {
			console.error(err);
			enqueueSnackbar('Save Failed', { variant: 'error' });
		}
	}

	/**
	 * Opens a popup to let the user select a Chime phone number.
	 */
	// function openChimeWindow() {
	//     setChimePhoneNumberDialogOpen(true);
	// }

	return (
		<>
			<Formik
				initialValues={genesysSettings}
				enableReinitialize={true}
				onSubmit={(values) => {
					saveTenantSettings(values, true);
				}}
			>
				{(formikProps) => (
					<Card style={cardStyle} sx={{ marginBottom: '24px' }} elevation={0}>
						<CardHeader title="Genesys" />

						<CardContent>
							<Typography variant="overline">Authorization</Typography>

							<form key="genesys" onSubmit={formikProps.handleSubmit}>
								<Box
									display="grid"
									gridTemplateColumns={useSingleColumn ? '1fr' : '1fr 1fr'}
									gap="20px"
								>
									<FormControl required>
										<InputLabel color="primary">Region</InputLabel>
										<Select
											color="primary"
											name="cloudRegion"
											value={formikProps.values.cloudRegion}
											onChange={formikProps.handleChange}
											onBlur={formikProps.handleBlur}
											required={true}
											disabled={testingConnection}
										>
											{Object.keys(PURE_CLOUD_REGION_HOSTS).map(
												(regionKey, index) => (
													<MenuItem
														key={index}
														value={PURE_CLOUD_REGION_HOSTS[regionKey]}
													>
														{regionKey}
													</MenuItem>
												),
											)}
										</Select>
										<FormHelperText>
											{formikProps.errors.region && formikProps.touched.region
												? formikProps.errors.region
												: 'The region where Genesys is hosted'}
										</FormHelperText>
									</FormControl>

									<TextField
										autoFocus
										color="primary"
										margin="dense"
										name="clientId"
										label="Client ID"
										type="text"
										required={true}
										onBlur={formikProps.handleBlur}
										value={formikProps.values.clientId}
										onChange={formikProps.handleChange}
										error={
											formikProps.errors.clientId &&
											formikProps.touched.clientId
										}
										helperText={formikProps.errors.clientId}
										disabled={testingConnection}
									/>

									<TextField
										autoFocus
										color="primary"
										margin="dense"
										name="clientSecret"
										label="Client Secret"
										type="password"
										required={true}
										onBlur={formikProps.handleBlur}
										value={formikProps.values.clientSecret}
										onChange={formikProps.handleChange}
										error={
											formikProps.errors.clientSecret &&
											formikProps.touched.clientSecret
										}
										helperText={formikProps.errors.clientSecret}
										disabled={testingConnection}
									/>
								</Box>
							</form>
						</CardContent>

						<CardActions>
							<Box
								width="100%"
								display="flex"
								flexDirection="row"
								justifyContent="end"
								alignItems="center"
							>
								{testingConnection && (
									<CircularProgress
										variant="indeterminate"
										color="primary"
										size="25px"
									/>
								)}
								<Button
									size="small"
									onClick={() => testConnection(formikProps.values)}
									disabled={
										formikProps.values.region === '' ||
										formikProps.values.organizationId === '' ||
										formikProps.values.clientId === '' ||
										formikProps.values.clientSecret === '' ||
										testingContactCenterConnection ||
										savingContactCenterValues ||
										userContext.securityOptions.connection < 2
									}
								>
									{testingConnection
										? 'Testing Connection...'
										: 'Test Connection'}
								</Button>
								{savingContactCenterValues && (
									<CircularProgress
										variant="indeterminate"
										color="primary"
										size="25px"
									/>
								)}
								<Button
									size="small"
									disabled={
										!formikProps.isValid ||
										testingConnection ||
										savingContactCenterValues ||
										!contactCenterConnectionSucceeded ||
										userContext.securityOptions.connection < 2
									}
									type="submit"
									color="primary"
									onClick={formikProps.handleSubmit}
								>
									{savingContactCenterValues ? 'Saving...' : 'Save'}
								</Button>
							</Box>
						</CardActions>
					</Card>
				)}
			</Formik>
		</>
	);
}
